import type { Action, PayloadAction } from '@reduxjs/toolkit';
import { ActionStatus, ApiExError, SessionType } from '@shared';
import { AdvisorHierarchyState } from './advisorHierarchyModel';
import { AiUnderwritingTypeformState } from './aiUnderwritingTypeformModel';
import { TableStateMap } from './dataTableModel';
import { LtcFinancialPlanningSliceState } from './ltcFinancialPlanningModel';
import { LtcPlanningQuotesState } from './ltcPlanningQuotesModel';
import { PolicyApplicationPrefillState } from './policyApplicationPrefillModel';
import { Quote, QuotesState } from './quotesModel';
import { SubscriptionSeatsState } from './subscriptionSeatsModel';
import { TypeformPayloadState } from './typeformPayloadModel';
import type { ImpersonationState } from './impersonationModel';

export const initialAdvisorOpportunitiesState = {
  status: null,
  errorMessage: null,
  qualifyingClients: [],
  priorityStatus: null,
  successfulEmails: [],
  failedEmails: [],
} as AdvisorOpportunityState;

export const initialClientEmailState = {
  status: {
    individualIntakeForm: null as ClientEmailUpdateStatus,
    batchIntakeForm: null as ClientEmailUpdateStatus,
    testEmail: null as ClientEmailUpdateStatus,
  },
  errorMessage: null as string | null,
  partialSuccessMessage: null as string | null,
  failedEmails: [] as string[],
};

export const initialState = {
  activeApiActions: [] as (Action<string> | PayloadAction<unknown>)[],
  advisor: {
    data: null as Advisor | null, // I don't think this is used anywhere anymore.
    advisorUpdates: {
      sendClientSigninLink: null as UpdateStatus,
      sendIntakeFormEngagementEmail: null as UpdateStatus,
      putAdvisorReferralSource: null as UpdateStatus,
      putClientAccessLinkEmail: null as UpdateStatus | 'unsubscribed',
      putAdvisorOrganization: null as UpdateStatus,
    },
    advisorSettings: {
      isLoading: false,
      status: null as 'complete' | 'error' | null,
    },
    isLoading: false,
  },
  client: {
    data: null as Client | null,
    clientPartner: null as Client | null,
    isInvalidating: false,
    isLoading: false,
    status: ActionStatus.idle,
    isPartialLoading: false,
    errorMessage: null as string | null,
    deletionStatus: null as ClientDeletionStatus | null,
    clientUpdates: {
      ltcAtAge: null as UpdateStatus,
      phaseDurations: null as UpdateStatus,
      advisorMeetingRequest: null as UpdateStatus,
      partnerLinkRequest: null as UpdateStatus,
      healthCareInflationRate: null as UpdateStatus,
      careEnvironmentSelections: null as UpdateStatus,
      careEnvironmentCosts: null as UpdateStatus,
    },
    phaseCosts: [] as SinglePhaseCosts[],
    supportProviderSet: null as SupportProviderSet | null,
    activeFundingSourceOperation: null as {
      fundingSourceId: string | null;
      insuranceQuoteId: string | null;
      operationType: 'add' | 'edit' | 'delete';
    } | null,
  },
  clients: {
    data: [] as Client[],
    isLoading: false,
    isInvalidating: false,
    clientDuplicationStatus: null as ActionStatus | null,
  },
  clientsSearchText: '',
  logTracker: {
    history: [] as string[],
  },
  session: {
    sessionType: SessionType.unknown,
    advisor: null,
    stripe: null,
    clientStripe: null,
    isLoading: false,
    isInvalidating: false,
    intercomIdentityHash: null,
    intercomUserType: null,
    error: null as string | null,
    oauth: {
      status: null as UpdateStatus,
      error: null as string | null,
      redirectInitiated: false,
    },
    clientProfiles: null as ClientProfile[] | null,
    preLogin: {
      email: null as string | null,
      status: null as UpdateStatus | null,
      error: null as string | null,
      connection: null as string | null,
      isClientUser: false,
    },
    advisorOnboardingStatus: null as AdvisorOnboardingStatusValue | null,
    seatStatus: null as string | null,
    forceRedirect: null as string | null,
    impersonation: null,
  } as Session,
  sponsorship: {
    activityAmount: 0,
    activityThreshold: 0,
    isLoading: false,
    isError: false,
  } as SponsorshipState,
  subscriptionSeats: {
    subscription: null,
    customer: null,
    seats: [],
    isOwner: false,
    ownerInfo: null,
    currentAdvisorAssignedDateTime: null,
    currentAdvisorExpiresDateTime: null,
    pendingSeatInvitations: null,

    // Fetch operations
    fetchStatus: 'idle',
    fetchError: null,

    // Mutation operations
    mutationStatus: 'idle',
    mutationError: null,
    currentMutation: null,

    // Invitation preview
    invitationPreviewStatus: 'idle',
    invitationPreviewError: null,
    invitationPreview: null,

    // Public invitation response (deprecated)
    seatInvitationResponse: {
      status: 'idle',
      error: null,
      message: null,
    },

    // Protected invitation accept response
    seatInvitationAcceptResponse: {
      status: 'idle',
      error: null,
      message: null,
    },

    // Protected invitation decline response
    seatInvitationDeclineResponse: {
      status: 'idle',
      error: null,
      message: null,
    },

    // Billing portal session
    billingPortal: {
      status: 'idle',
      error: null,
      url: null,
    },
  } as SubscriptionSeatsState,
  clientEmail: {
    status: initialClientEmailState.status,
    errorMessage: null as string | null,
    partialSuccessMessage: null as string | null,
    failedEmails: [] as string[],
  },
  emailPreferences: {
    email: '',
    optOutReminders: false,
    optOutTransactions: false,
    isLoading: false,
    error: null as string | null,
  },
  featureFlags: {
    flags: {
      vbShop: false,
      transamerica: false,
      fortifiedLife: false,
      newYorkLife: false,
      ltciPartners: false,
      agis: false,
    },
  },
  appConfigFeatureFlags: {
    flags: {} as AppConfigFeatureFlags,
    isLoading: false,
    isLoaded: false,
  },
  policyExtractor: {
    status: null,
    errors: [],
    requestId: '',
    requestedRuns: 0,
    currentRunPollCount: 0,
    fileUrl: '',
    fileBucket: '',
    fileKey: '',
    runsCompletedOrFailed: 0,
    responses: [],
    parsedResponses: [],
    processedResponse: null,
    policyType: null,
    uploadMode: 'pdf',
    imagePresignedUrls: [],
    imageBucket: '',
    imageKeys: [],
  } as PolicyExtractorState,
  illustration: {
    activeRequests: {},
    completedIllustrations: {},
    erroredRequests: {},
    errors: [],
    hasActiveRequest: false,
    shownErrorToasts: {},
  } as IllustrationState,
  dataTable: {} as TableStateMap,
  resendTemporaryPasswordEmail: {
    isLoading: false,
    error: false,
    success: false,
  },
  verifyPartnerLinkRequest: {
    status: null as null | 'loading' | 'complete' | 'error',
    errorMessage: null as string | null,
  },
  advisorHierarchy: {
    subordinateAdvisors: null,
    status: null,
    errorMessage: null,

    invitationMutationStatus: 'idle',
    invitationMutationError: null,

    deleteMutationStatus: 'idle',
    deleteMutationError: null,

    directRelationshipMutationStatus: 'idle',
    directRelationshipMutationError: null,

    relationships: null,
    relationshipsFetchStatus: 'idle',
    relationshipsFetchError: null,

    supervisorAdvisorRelationships: null,
    pendingSupervisorInvitations: null,

    invitationAcceptResponse: {
      status: 'idle',
      error: null,
      message: null,
    },

    invitationDeclineResponse: {
      status: 'idle',
      error: null,
      message: null,
    },
  } as AdvisorHierarchyState,
  advisorAdminPortal: {
    status: null as null | 'loading' | 'success' | 'error',
    errorMessage: null as string | null,
    successMessage: null as string | null,
    selectedAdvisor: null as Advisor | null,
    advisorFetchStatus: null as null | 'loading' | 'success' | 'error',
    advisorFetchError: null as string | null,
  },
  advisorOpportunities: {
    FILL_OUT_INTAKE_FORM: initialAdvisorOpportunitiesState,
    REVIEW_RESULTS_TOGETHER: initialAdvisorOpportunitiesState,
    RESEND_CLIENT_SIGNIN_LINKS: initialAdvisorOpportunitiesState,
    QAA_ROI_PROJECTION_LINKS: initialAdvisorOpportunitiesState,
  } as Record<ClientEmailEngagement, AdvisorOpportunityState>,
  advisorBgaCarrierSettings: {
    bgas: {
      data: [] as BgaEntry[],
      status: null as UpdateStatus,
    },
    carriers: {
      data: [] as InsuranceCarrierWithPolicyTypes[],
      status: null as UpdateStatus,
    },
  },
  stripeSubscribeSession: {
    isLoading: false,
    error: false,
    success: false,
    url: null as string | null,
    clientSecret: null as string | null,
  },
  quotes: {
    data: [] as Quote[],
    isLoading: false,
    error: null as string | null,
  } as QuotesState,
  policyApplicationModel: {
    applications: [] as ClientPolicyApplication[],
    status: null as PolicyApplicationModelStatus,
    error: null as string | null,
    embeddedSigningData: null as EmbeddedSigningViewResponse | null,
    lastCreatedApplicationId: null as string | null,
    formIdCache: {} as Record<string, string>,
    pendingFormFetchCacheKey: null as string | null,
    formConfigCache: {} as Record<string, PolicyFormConfig>,
    pendingFormIdFetch: null as string | null,
    docusignEnvelopeStatus: null as string | null,
    docusignSubmissionDataComparison: null as DocusignSubmissionDataComparisonResponse | null,
  },
  openAiCompletion: {
    completions: {} as Record<string, OpenAiCompletionDbRecord>,
    errors: {} as Record<string, ApiExError>,
    currentRequestId: null as string | null,
  },
  openAiCompletionSearch: {
    data: null as OpenAiCompletionDbRecord | null,
    error: null as ApiExError | null,
    isLoading: false,
  },
  concurrentSearch: {
    searches: {} as Record<
      string,
      {
        data: OpenAiCompletionDbRecord | null;
        error: ApiExError | null;
        isLoading: boolean;
      }
    >,
  },
  partnerPolicyApplicationModel: {
    applications: [] as ClientPolicyApplication[],
    status: null as PolicyApplicationModelStatus,
    error: null as string | null,
  },
  typeformPayload: {
    data: null as TypeformPayload,
    isLoading: false,
    error: null as string | null,
  } as TypeformPayloadState,
  policyApplicationPrefill: {
    entries: [],
  } as PolicyApplicationPrefillState,
  aiUnderwritingTypeform: {
    data: {
      client: null as TypeformPayload | null,
      partner: null as TypeformPayload | null,
    },
    isLoading: {
      client: false,
      partner: false,
    },
    error: {
      client: null as string | null,
      partner: null as string | null,
    },
  } as AiUnderwritingTypeformState,
  consent: {
    applicationConsentStatus: {
      data: null,
      isLoading: false,
      errorMessage: null,
    },
    postConsentRecord: {
      data: null,
      isLoading: false,
      errorMessage: null,
    },
    sendConsentNotificationEmail: {
      data: null,
      isLoading: false,
      errorMessage: null,
    },
    postRecordCarePacketDownload: {
      data: null,
      isLoading: false,
      errorMessage: null,
    },
  } as ConsentState,
  advisorOnboarding: {
    data: null as AdvisorOnboardingStatus | null,
    status: null as UpdateStatus,
    hasSkippedOnboardingStep: false,
  },
  ltcFinancialPlanning: {
    policyPreferences: {
      deathBenefit: null,
      paymentStructure: null,
      benefitStructure: null,
    },
    selectedPolicyTypes: [],
    selectedAccountTypes: [],
    currentStep: 1,
    isLoading: false,
  } as LtcFinancialPlanningSliceState,
  ltcPlanningQuotes: {
    data: [],
    isLoading: false,
    error: null,
    lastFetchedAt: null,
  } as LtcPlanningQuotesState,
  usageAnalytics: {
    data: null,
    isLoading: false,
    errorMessage: null,
  } as UsageAnalyticsState,
  impersonation: {
    targetAdvisorId: null,
  } as ImpersonationState,
};
